import { motion } from "framer-motion";
import _ from "lodash";
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { PlusIcon, XIcon } from "lucide-react";
import QRCode from "react-qr-code";
import { StoreApi, UseBoundStore } from "zustand";

import {
  DocumentEnum,
  DocumentFragment,
  DocumentTemplateLayoutEnum,
  PublicDocumentFragment,
} from "@/__generated__/graphql";
import {
  FieldDefinitionType,
  FieldEventsType,
  NodeStore,
  TableColumnType,
} from "@/components/@types";
import { FileDropzone } from "@/components/molecules/FileDropzone";
import { PdfPreview } from "@/components/molecules/PdfPreview";
import { FieldCell } from "@/components/organisms/Field/FieldCell";
import { useNodeField } from "@/components/organisms/Node/Node.hooks";
import { Mutations } from "@/constants";
import { TagColors } from "@/constants/values";
import { useBackendMutation } from "@/hooks/useBackendMutation";
import {
  useContextCellNavigation,
  useContextScope,
  type CellProps,
} from "@/hooks/useContextTree";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { Typography, typographyVariants } from "@/components/ui/typography";
import { cn } from "@/components/ui/utils";
import {
  INVOICE_LABELS,
  type InvoiceTemplate,
  TEMPLATE_FONTS,
} from "./DocumentPreview.content";
import { useInvoicePdf } from "./DocumentPreviewInvoicePDF";
import { describeInvoice, type PrintedLine } from "./DocumentPreview.utils";

type PreviewLineItem = {
  id?: string | null;
  identifier?: string | null;
  metadata?: Record<string, unknown> | null;
  amount?: number | string | null;
  amountSubtotal?: number | string | null;
  amountTax?: number | string | null;
  description?: string | null;
  quantity?: number | null;
  unitPrice?: number | null;
  tax?: {
    id?: string | null;
    identifier?: string | null;
    name?: string | null;
    totalRate?: number | string | null;
    rates?: ({ rate?: number | string | null } | null)[] | null;
  } | null;
  chartedAccount?: {
    id?: string | null;
    identifier?: string | null;
    name?: string | null;
  } | null;
};

type PreviewDocument = (DocumentFragment | PublicDocumentFragment) & {
  /** Whether the page it is shared at takes a card for it, as the server says. */
  canAcceptPayments?: boolean | null;
  lineItems?: PreviewLineItem[] | null;
  amountDiscounted?: number | null;
  amountTax?: number | null;
  payableFrom?: string | null;
  payableTo?: string | null;
};

type AttachedFile = NonNullable<DocumentFragment["file"]>;

export function DocumentAttachedFilePreview({
  file,
  highlight,
}: {
  file: AttachedFile;
  /** What to mark in a PDF where it has it: see ``PdfPreview``. */
  highlight?: RegExp[];
}) {
  const fileName = file.name?.toLowerCase() ?? "";
  const isPdf = fileName.endsWith(".pdf");
  const isImage = /\.(avif|bmp|gif|jpe?g|png|svg|webp)$/.test(fileName);
  const isHtml = /\.html?$/.test(fileName);

  return (
    <div className="flex flex-col w-full min-h-120 bg-muted">
      {isImage ? (
        <img
          src={file.url}
          alt={file.name || "Document preview"}
          className="w-full max-h-[calc(100vh-220px)] object-contain bg-background"
        />
      ) : isPdf ? (
        <PdfPreview key={file.url} url={file.url} highlight={highlight} />
      ) : isHtml ? (
        <iframe
          src={file.url}
          title={file.name || "Document preview"}
          /**
           * Forwarded email bodies are archived as HTML, so this frame renders
           * markup a third party wrote. The signed storage URL already puts it
           * on its own origin, and the sandbox stops it running script or
           * navigating the tab out from under the panel.
           */
          sandbox=""
          referrerPolicy="no-referrer"
          className="w-full min-h-[calc(100vh-220px)] [border:0px_solid] bg-background"
        />
      ) : (
        <div className="flex flex-col items-center justify-center gap-3 flex-1 p-12 text-center">
          <Typography level="title-md" className="text-foreground">
            {file.name || "Attached file"}
          </Typography>
          <Typography level="body-sm" className="text-muted-foreground">
            Preview is not available for this file type.
          </Typography>
          <a
            href={file.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary underline-offset-4 hover:underline"
          >
            Open file
          </a>
        </div>
      )}
    </div>
  );
}

type PreviewStore = UseBoundStore<StoreApi<NodeStore>>;

/**
 * Invoice number, billed to and the description sit above the line items, so
 * they are rows 0–2 of the canvas navigation grid and the lines start at row 3.
 */
const HEADER_ROW_COUNT = 3;

/**
 * Every header value sits in the same box whether it is an editable field, a
 * reference chip or plain text — otherwise the slots differ in height purely by
 * what happens to be inside them (a text input rendered nearly twice the height
 * of the contact chip beside it). The slot spans its whole grid column too: a
 * shrink-to-fit box clipped long values (a contact chip) well short of the
 * space the column actually had.
 */
const HEADER_SLOT_STYLE = {
  display: "flex",
  alignItems: "center",
  minHeight: 36,
  width: "100%",
  minWidth: 0,
} as const;

/**
 * One invoice-canvas slot bound to a node-store field.
 *
 * The canvas reuses the ``DocumentObject`` field definitions verbatim — type,
 * placeholder and reference options all come from the store — so an edit made
 * here is identical to the same edit made in the Overview group: pending
 * operations, optimistic updates and the delta view keep working untouched.
 * Fields the object marks read-only (e.g. ``reference``) render as plain,
 * non-editable text.
 */
function DocumentPreviewField({
  store,
  objectKey,
  isMultiline = false,
  style,
  cellProps,
  printedAs,
}: {
  store: PreviewStore;
  objectKey: string;
  isMultiline?: boolean;
  style?: React.CSSProperties;
  /**
   * Registers the slot in the canvas navigation grid, so arrow keys and Tab
   * carry on from the header into the line items rather than stopping at the
   * first cell of the table.
   */
  cellProps?: CellProps;
  /** What a reference's chip says: the invoice's words for it, as it prints. */
  printedAs?: string;
}) {
  const { fieldDefinition, fieldEvents, fieldValues } = useNodeField(
    store,
    objectKey,
  );

  if (!fieldDefinition) {
    return null;
  }
  const getValue: FieldDefinitionType["getValue"] =
    printedAs === undefined
      ? fieldDefinition.getValue
      : (((data: unknown) => {
          const value = (
            fieldDefinition.getValue as (data: unknown) => {
              id?: string | null;
            } | null
          )(data);
          return value?.id ? { ...value, name: printedAs } : value;
        }) as FieldDefinitionType["getValue"]);

  // The notes block stays expanded — a document body wrapped like print rather
  // than clipped to one line — but it is the same ``FieldCell`` as every other
  // slot, so it registers in the canvas grid and takes the interaction lock
  // without a parallel code path.
  return (
    <div
      className={cn(
        isMultiline ? "w-full" : "flex items-center min-h-9 w-full min-w-0",
      )}
    >
      <FieldCell
        type={fieldDefinition.type}
        getValue={getValue}
        getValueOptions={fieldDefinition.getValueOptions}
        data={fieldValues}
        store={store}
        fieldEvents={(fieldEvents || undefined) as FieldEventsType}
        isEditable={Boolean(fieldEvents)}
        isSingleClick
        isMultiline={isMultiline}
        isExpanded={isMultiline || undefined}
        size="md"
        style={isMultiline ? { whiteSpace: "pre-wrap", ...style } : style}
        slotProps={{
          cell: {
            ...cellProps,
            radius: "calc(var(--radius) - 4px)",
            style: {
              ...(isMultiline
                ? {
                    display: "flex",
                    alignItems: "flex-start",
                    width: "100%",
                    minWidth: 0,
                    // Same floor as the single-line slots: an empty or one-line
                    // description sat visibly shorter than the invoice number
                    // field above it.
                    minHeight: HEADER_SLOT_STYLE.minHeight,
                  }
                : HEADER_SLOT_STYLE),
              marginLeft: -4,
              marginRight: -4,
              paddingLeft: 4,
              paddingRight: 4,
              maxWidth: "100%",
            },
          },
        }}
      />
    </div>
  );
}

/**
 * Editable invoice lines.
 *
 * Every cell writes through the ``lineItems`` field events — the same contract
 * ``FieldTable`` uses — so the Line Items grid, the pending-change delta view
 * and the submit payload all stay in sync with what is typed on the canvas.
 * When the object marks ``lineItems`` read-only (``get`` mode) the same rows
 * render without events, i.e. as plain text in the identical layout.
 */
function DocumentPreviewLineItems({
  store,
  printed,
  getCellProps,
}: {
  store: PreviewStore;
  /** Each row as the invoice prints it, by its place. */
  printed: PrintedLine[];
  /** Canvas-wide cell registry — the header slots occupy the rows above. */
  getCellProps: (params: {
    rowIndex: number;
    colIndex: number;
    objectKey?: string;
  }) => CellProps;
}) {
  const { fieldDefinition, fieldEvents, fieldValues } = useNodeField(
    store,
    "lineItems",
  );
  // The live field values are the current truth: the node store's reconcile
  // keeps them merged with server refreshes, and the Line Items grid no longer
  // overwrites them with a stale snapshot. Deriving from the server baseline
  // instead would follow it backwards whenever a cached response lands late.
  const rows = (fieldDefinition?.getValue?.(fieldValues) ??
    []) as PreviewLineItem[];
  const columns = ((
    fieldDefinition?.getValueOptions?.(fieldValues) as
      | { columns?: TableColumnType[] }
      | undefined
  )?.columns ?? []) as TableColumnType[];
  const descriptionColumn = columns.find(
    (column) => column.objectKey === "description",
  );
  const quantityColumn = columns.find(
    (column) => column.objectKey === "quantity",
  );
  const unitPriceColumn = columns.find(
    (column) => column.objectKey === "unitPrice",
  );
  const taxColumn = columns.find((column) => column.objectKey === "tax");
  const onChangeRows = fieldEvents
    ? (fieldEvents.onChangeField as (value: PreviewLineItem[]) => void)
    : null;

  // Every write starts from the store as it is *now*, never from the rows this
  // render closed over. The two differ for a moment after the line items load
  // (they arrive separately from the node), and writing the stale array there
  // dropped the rows the user had not seen yet — which a save then deleted.
  const currentRows = () =>
    (fieldDefinition?.getValue?.(store.getState().fieldValues) ??
      []) as PreviewLineItem[];

  // Tabbing across a row blurs every cell it passes through, and each blur
  // would otherwise submit the whole document. Coalesce them the way
  // ``FieldTable`` does, so a pass over a line is one mutation rather than four
  // overlapping ones whose responses race each other back.
  const submitRef = useRef(fieldEvents ? fieldEvents.onSubmitField : undefined);
  submitRef.current = fieldEvents ? fieldEvents.onSubmitField : undefined;
  const submitLineItems = useMemo(
    () => _.debounce(() => submitRef.current?.(), 1200),
    [],
  );
  useEffect(() => () => submitLineItems.flush(), [submitLineItems]);

  return (
    <>
      {rows.map((row, rowIndex) => {
        // Cells are described inline rather than extracted so the file reads in
        // one pass from "what the invoice shows" to "what the store stores".
        const cells = [
          {
            key: "description",
            type: "text" as const,
            isMultiline: true,
            align: "flex-start",
            getValue: () => row.description ?? "",
            getValueOptions: descriptionColumn?.getValueOptions,
            onChangeField: (value: unknown) =>
              onChangeRows?.(
                currentRows().map((current, index) =>
                  index === rowIndex
                    ? { ...current, description: value as string }
                    : current,
                ),
              ),
          },
          {
            key: "quantity",
            type: "number" as const,
            isMultiline: false,
            align: "flex-end",
            getValue: () => quantityColumn?.getValue?.(row),
            getValueOptions: quantityColumn?.getValueOptions,
            // The Line Items grid's own cell, written as the grid writes it:
            // the quantity scales the amount at the line's unit price.
            onChangeField: (value: unknown) =>
              onChangeRows?.(
                currentRows().map((current, index) =>
                  index === rowIndex
                    ? {
                        ...(quantityColumn?.preprocessOnChange?.(
                          "quantity",
                          value,
                          current,
                        ) ?? current),
                        quantity: value as number,
                      }
                    : current,
                ),
              ),
          },
          {
            key: "unitPrice",
            type: "currency" as const,
            isMultiline: false,
            align: "flex-end",
            getValue: () => unitPriceColumn?.getValue?.(row),
            getValueOptions: unitPriceColumn?.getValueOptions,
            // ``amount`` holds the line subtotal while the invoice shows it per
            // unit: the grid's column writes it from the price and quantity.
            onChangeField: (value: unknown) =>
              onChangeRows?.(
                currentRows().map((current, index) =>
                  index === rowIndex
                    ? {
                        ...(unitPriceColumn?.preprocessOnChange?.(
                          "unitPrice",
                          value,
                          current,
                        ) ?? current),
                        unitPrice: value as number,
                      }
                    : current,
                ),
              ),
          },
          {
            key: "tax",
            type: "reference" as const,
            isMultiline: false,
            align: "flex-end",
            // The canvas has no column for the rate, so the chip carries it,
            // as the invoice prints it: "GST (5%)" rather than a bare name the
            // reader cannot price.
            getValue: () => {
              const value = taxColumn?.getValue?.(row) as
                | { name?: string | null }
                | undefined;
              return value?.name
                ? { ...value, name: printed[rowIndex]?.tax }
                : value;
            },
            getValueOptions: taxColumn?.getValueOptions,
            onChangeField: (value: unknown) =>
              onChangeRows?.(
                currentRows().map((current, index) =>
                  index === rowIndex
                    ? { ...current, tax: value as PreviewLineItem["tax"] }
                    : current,
                ),
              ),
            // The line items grid gives its tax chip a clear button; without
            // the same here the canvas chip rendered no X while still making
            // room for one on hover, so the label ellipsised for a button that
            // never arrived.
            onDeleteField: () =>
              onChangeRows?.(
                currentRows().map((current, index) =>
                  index === rowIndex ? { ...current, tax: null } : current,
                ),
              ),
          },
        ];

        // The remove button is the row's last cell, so arrow-right off the tax
        // column lands on it instead of the row dead-ending at the last
        // editable field.
        const removeCellProps = getCellProps({
          rowIndex: rowIndex + HEADER_ROW_COUNT,
          colIndex: cells.length,
          objectKey: "remove",
        });

        return (
          <div
            // ``_frontend_uuid`` is the row's identity from creation onwards —
            // it now survives the round trip, so the key does not change when a
            // save comes back and the row is not remounted mid-edit. Position
            // would be wrong here: deleting a row shifts every key after it.
            key={
              (row.metadata?._frontend_uuid as string) ||
              row.identifier ||
              row.id ||
              rowIndex
            }
            className={cn(
              // Cells stretch and the row grows with the tallest one — a long
              // description wraps onto further lines instead of being clipped
              // to one.
              "grid grid-cols-[1.5fr_0.5fr_0.9fr_1.3fr_1fr_28px] [align-items:start] gap-4 pt-2 pb-2 [border-bottom-width:1px] [border-bottom-style:solid] border-border",
              "[&:hover_.document-preview-line-remove]:opacity-100",
            )}
          >
            {cells.map((cell, colIndex) => (
              <div
                key={cell.key}
                className="flex [justify-content:var(--align)] min-w-0 overflow-hidden"
                style={{ "--align": cell.align } as CSSProperties}
              >
                <FieldCell
                  type={cell.type}
                  // Multiline cells stay expanded so the row reflects the whole
                  // value, not a single clipped line.
                  isExpanded={cell.isMultiline || undefined}
                  getValue={cell.getValue as FieldDefinitionType["getValue"]}
                  getValueOptions={cell.getValueOptions}
                  data={row}
                  store={store}
                  fieldEvents={
                    onChangeRows
                      ? {
                          onChangeField: cell.onChangeField,
                          ...(cell.onDeleteField
                            ? { onDeleteField: cell.onDeleteField }
                            : {}),
                          onSubmitField: submitLineItems,
                          onInteractionStart: fieldEvents?.onInteractionStart,
                          onInteractionEnd: fieldEvents?.onInteractionEnd,
                        }
                      : undefined
                  }
                  isEditable={Boolean(onChangeRows)}
                  isSingleClick
                  isMultiline={cell.isMultiline}
                  size="md"
                  style={
                    cell.align === "flex-end"
                      ? { textAlign: "right" }
                      : undefined
                  }
                  slotProps={{
                    // The content layer fills the cell, so a chip or an add
                    // button inside it needs pushing right of its own accord.
                    ...(cell.type === "reference" && cell.align === "flex-end"
                      ? {
                          content: {
                            style: {
                              display: "flex",
                              justifyContent: "flex-end",
                            },
                          },
                        }
                      : {}),
                    // ``FieldCell`` defaults to ``fit-content`` so a grid cell
                    // never clips mid-word; on the invoice the column widths
                    // are the layout, so cells shrink and ellipsize instead.
                    cell: {
                      ...getCellProps({
                        rowIndex: rowIndex + HEADER_ROW_COUNT,
                        colIndex,
                        objectKey: cell.key,
                      }),
                      radius: "calc(var(--radius) - 4px)",
                      style: {
                        minHeight: 32,
                        // No inset of its own: the value's edge is the column
                        // heading's edge, as on the printed invoice.
                        paddingLeft: 0,
                        paddingRight: 0,
                        minWidth: 0,
                        maxWidth: "100%",
                        // ``textAlign`` right-aligns text but not a chip or an
                        // add button, which are laid out rather than flowed.
                        ...(cell.align === "flex-end"
                          ? {
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "flex-end",
                            }
                          : {}),
                      },
                    },
                  }}
                />
              </div>
            ))}

            {/* The row grows with its tallest cell (a wrapped description), and
                every cell sits on the row's first line: the same 32px line box
                the editable cells centre their value in. Centred in the whole
                row instead, the total dropped below the line it belongs to. */}
            <div className="flex items-center justify-end min-h-8">
              <Typography
                level="body-md"
                className="text-foreground text-right"
              >
                {printed[rowIndex]?.total}
              </Typography>
            </div>

            {onChangeRows && (
              <div className="flex items-center min-h-8">
                <Button
                  variant="ghost"
                  size="icon-xs"
                  {...removeCellProps}
                  className={cn(
                    `document-preview-line-remove ${
                      removeCellProps.className ?? ""
                    }`,
                    "opacity-[0] [transition:opacity_0.1s_ease-in-out] focus-visible:opacity-[1]",
                  )}
                  aria-label="Remove line item"
                  data-testid="document-preview-remove-line-item"
                  // Navigating onto it with the keyboard has to show it too —
                  // otherwise focus lands on a button nobody can see.
                  onClick={() => {
                    onChangeRows(
                      currentRows().filter((_row, index) => index !== rowIndex),
                    );
                    submitLineItems();
                    submitLineItems.flush();
                  }}
                >
                  <XIcon size={16} />
                </Button>
              </div>
            )}
          </div>
        );
      })}

      {rows.length === 0 && !onChangeRows && (
        <div className="pt-6 pb-6 text-center [border-bottom-width:1px] [border-bottom-style:solid] border-border">
          <Typography level="body-md" className="text-muted-foreground/80">
            {INVOICE_LABELS.noLines}
          </Typography>
        </div>
      )}

      {onChangeRows && (
        <Button
          variant="outline"
          size="sm"
          data-testid="document-preview-add-line-item"
          // The button is the row after the last line, so arrow keys and Tab
          // run off the end of the table straight onto it.
          {...getCellProps({
            rowIndex: rows.length + HEADER_ROW_COUNT,
            colIndex: 0,
            objectKey: "addLineItem",
          })}
          onClick={() => {
            onChangeRows([
              ...currentRows(),
              {
                metadata: { _frontend_uuid: crypto.randomUUID() },
                description: "",
                quantity: 1,
                amount: 0,
                amountSubtotal: 0,
              } as PreviewLineItem,
            ]);
          }}
          className="w-fit mt-2 pl-1 pr-1"
        >
          <PlusIcon size={18} />
          Add line item
        </Button>
      )}
    </>
  );
}

/**
 * Takes the file for a document that arrived without one. A document's file is
 * write-once — a receipt *is* its scan — so this only ever appears where there
 * is nothing to replace, and disappears once the upload lands.
 */
function DocumentFileDropTarget({
  documentId,
  isEditable,
}: {
  documentId?: string | null;
  isEditable: boolean;
}) {
  const [error, setError] = useState<string | null>(null);
  const { mutate, loading } = useBackendMutation({
    mutation: Mutations.ATTACH_DOCUMENT_FILE,
    callbacks: {
      onError: () => setError("That file could not be attached. Try again."),
    },
  });

  if (!documentId || !isEditable) {
    return (
      <div className="flex items-center justify-center flex-1 min-h-80 h-full p-12">
        <Typography level="body-sm" className="text-muted-foreground/80">
          No file attached.
        </Typography>
      </div>
    );
  }

  return (
    // Centred in the preview area: with nothing to show yet, the invitation to
    // add something is the whole content of the panel.
    <div className="flex flex-col items-center justify-center gap-1 flex-1 min-h-80 h-full p-6 w-full">
      <Typography level="title-md" className="text-foreground">
        No file attached
      </Typography>
      <Typography level="body-sm" className="text-muted-foreground mb-2">
        Drop the receipt or bill here to keep it with this document.
      </Typography>
      {loading ? (
        <div className="p-12 text-center">
          <Typography level="body-sm" className="text-muted-foreground/80">
            Attaching…
          </Typography>
        </div>
      ) : (
        <FileDropzone
          multiple={false}
          hint="PDFs and images"
          onDrop={(files) => {
            const [file] = files;
            if (!file) return;
            setError(null);
            void mutate({
              variables: { input: { id: documentId, file } },
            });
          }}
        />
      )}
      {error && (
        <Typography level="body-sm" className="text-destructive mt-2">
          {error}
        </Typography>
      )}
    </div>
  );
}

export function DocumentPreview({
  document,
  isLoading = false,
  publicUrl,
  store,
  template,
}: {
  document: PreviewDocument;
  isLoading?: boolean;
  publicUrl?: string;
  /** The template an invoice prints with: its look, and how it is paid. */
  template?: InvoiceTemplate | null;
  /**
   * When set, the invoice is its canvas: header slots and line items bind to
   * this node store, editable where the record is. Read where nobody edits it,
   * an invoice is its PDF, drawn from the same words as its canvas.
   */
  store?: PreviewStore;
}) {
  const attachedFile =
    document && "file" in document ? document.file : undefined;

  // Which branch to show depends on the document's type, so there is nothing
  // honest to render until it has loaded — picking one early flashes an invoice
  // canvas or a "no file attached" prompt at someone whose document is neither.
  if (isLoading) {
    return (
      <div className="flex flex-col gap-4 w-full p-6">
        <Skeleton variant="text" level="h1" className="w-[40%]" />
        <div className="flex gap-8">
          <Skeleton variant="text" level="body-md" className="w-[30%]" />
          <Skeleton variant="text" level="body-md" className="w-[30%]" />
        </div>
        <Skeleton variant="text" level="h2" className="w-[55%]" />
        <Skeleton variant="rectangular" className="w-full h-30" />
      </div>
    );
  }

  // An invoice made here is a document this app composes, so it renders as
  // one. Anything that came in as a file — a receipt, a bill, an invoice
  // someone sent — *is* that file, so the file is the preview. A document of
  // that kind with no file yet has nothing to show, and offers to take one.
  const isUnsaved = !document?.id;
  if (
    (document?.documentType === DocumentEnum.Invoice && !attachedFile?.url) ||
    isUnsaved
  ) {
    return store ? (
      <InvoiceDocumentPreview
        document={document}
        publicUrl={publicUrl}
        store={store}
        template={template}
      />
    ) : (
      <InvoicePdfPreview
        document={document}
        publicUrl={publicUrl}
        template={template}
      />
    );
  }

  if (attachedFile?.url) {
    return <DocumentAttachedFilePreview file={attachedFile} />;
  }

  return (
    <DocumentFileDropTarget
      documentId={document?.id}
      isEditable={Boolean(store)}
    />
  );
}

/**
 * An invoice where nobody edits it: its PDF, worded as its canvas is - as its
 * page shows it, and hands it to whoever downloads it.
 */
function InvoicePdfPreview({
  document,
  publicUrl,
  template,
}: {
  document: PreviewDocument;
  publicUrl?: string;
  template?: InvoiceTemplate | null;
}) {
  const invoice = useMemo(
    () =>
      describeInvoice({
        document,
        lines: document?.lineItems ?? [],
        template,
        canAcceptPayments: Boolean(document?.canAcceptPayments),
        publicUrl,
      }),
    [document, template, publicUrl],
  );
  const file = useInvoicePdf(invoice, template);
  return <PdfPreview url={file?.url ?? null} />;
}

function InvoiceDocumentPreview({
  document,
  publicUrl,
  store,
  template,
}: {
  document: PreviewDocument;
  publicUrl?: string;
  store: PreviewStore;
  template?: InvoiceTemplate | null;
}) {
  // Its template's look: its heading on a band of its colour, or set closer
  // together; the colour its heading, rules and what is due are drawn in; its
  // typeface. One with no template is drawn as the app draws an invoice.
  const isBanner = template?.layout === DocumentTemplateLayoutEnum.Banner;
  const isCompact = template?.layout === DocumentTemplateLayoutEnum.Compact;
  const accent = template?.color
    ? TagColors[template.color].solidColor
    : undefined;
  const typeface = template?.font
    ? TEMPLATE_FONTS[template.font].screen
    : undefined;
  // On the band, its words are drawn over its colour.
  const headingClassName = isBanner
    ? "text-[#fff]"
    : accent
      ? "text-(--invoice-accent)"
      : "text-foreground";
  const headingLabelClassName = isBanner
    ? "text-[rgba(255,255,255,0.75)]"
    : "text-muted-foreground/80";
  const headingValueClassName = isBanner ? "text-[#fff]" : "text-foreground";
  // What is drawn in its colour, else as the app draws it.
  const accentTextClassName = accent
    ? "text-(--invoice-accent)"
    : "text-foreground";
  // What it says, worded as its PDF words it - its lines as they are being
  // edited, each one's tax as its rates make it, which the server works out
  // the same once it is saved. How it is paid is as the server says the page
  // it is shared at takes it: by e-transfer as its template tells the payer,
  // by card where that page takes one.
  const { fieldDefinition: lineItemsDefinition, fieldValues } = useNodeField(
    store,
    "lineItems",
  );
  const rows = (lineItemsDefinition?.getValue?.(fieldValues) ??
    []) as PreviewLineItem[];
  const amountTaxColumn = (
    lineItemsDefinition?.getValueOptions?.(fieldValues) as
      | { columns?: TableColumnType[] }
      | undefined
  )?.columns?.find((column) => column.objectKey === "amountTax");
  const invoice = describeInvoice({
    document,
    lines: rows.map((row) => ({
      ...row,
      amountTax: (amountTaxColumn?.getValue?.(row) ?? row.amountTax) as
        | number
        | null,
    })),
    template,
    canAcceptPayments: Boolean(document?.canAcceptPayments),
    publicUrl,
  });
  // One registry for the whole canvas: the header slots are its first rows and
  // the line items follow, so arrow keys and Tab run from the invoice number
  // straight through the table instead of the header being a set of unrelated
  // inputs. Row and column bounds come from what actually registers, which is
  // what lets a one-cell header row sit above four-cell line rows.
  const {
    id: navigationContextId,
    Scope,
    ref: scopeRef,
    node: scopeNode,
  } = useContextScope({});
  const { getCellProps } = useContextCellNavigation(
    navigationContextId,
    scopeRef,
    scopeNode,
    { tab: true },
  );

  return (
    // ``display: contents`` so registering the canvas as a navigation scope
    // adds no box of its own to the invoice layout.
    <Scope style={{ display: "contents" }}>
      <div
        data-testid="invoice-canvas"
        data-layout={template?.layout ?? undefined}
        className={cn(
          "flex flex-col bg-popover w-full",
          typeface
            ? // Every line of it in its template's typeface: the theme names a
              // face for each level of text, so each is named over.
              "font-(family-name:--invoice-typeface) [&_[data-slot=typography]]:font-(family-name:--invoice-typeface) [&_input]:font-(family-name:--invoice-typeface) [&_textarea]:font-(family-name:--invoice-typeface)"
            : "font-medium",
          isCompact ? "p-4" : "p-6",
        )}
        style={
          {
            "--invoice-accent": accent,
            "--invoice-typeface": typeface ?? undefined,
          } as CSSProperties
        }
      >
        {/* Header: title, then meta grid, one hairline - or on a band */}
        <div
          className={cn(
            "flex flex-col gap-2 w-full pb-3 mb-3",
            isBanner
              ? "bg-(--invoice-accent) -mx-6 -mt-6 px-6 pt-6 w-auto"
              : [
                  "[border-bottom-width:1px] [border-bottom-style:solid]",
                  accent ? "border-(--invoice-accent)" : "border-border",
                ],
          )}
        >
          <Typography
            level="h1"
            className={cn(
              isCompact ? "text-[1.75rem]" : "text-[2.5rem]",
              "[font-weight:600] [letter-spacing:-0.02em] [line-height:1.15]",
              headingClassName,
            )}
          >
            {INVOICE_LABELS.title}
          </Typography>

          <div className="grid grid-cols-[repeat(auto-fit,minmax(10rem,1fr))] gap-3 w-full min-w-0">
            <div className="min-w-0">
              <Typography
                level="body-sm"
                className={cn(headingLabelClassName, "mb-0.5")}
              >
                {INVOICE_LABELS.number}
              </Typography>
              <DocumentPreviewField
                store={store}
                objectKey={document?.reference ? "reference" : "name"}
                style={{
                  overflowWrap: "anywhere",
                  ...(isBanner ? { color: "#fff" } : {}),
                }}
                cellProps={getCellProps({
                  rowIndex: 0,
                  colIndex: 0,
                  objectKey: "reference",
                })}
              />
            </div>
            <div className="min-w-0">
              <Typography
                level="body-sm"
                className={cn(headingLabelClassName, "mb-0.5")}
              >
                {INVOICE_LABELS.issued}
              </Typography>
              <Typography
                level="body-md"
                className={cn(
                  "flex items-center min-h-9 w-full min-w-0",
                  headingValueClassName,
                )}
              >
                {invoice.issued}
              </Typography>
            </div>
          </div>
        </div>

        {/* Billed To / Issued By */}
        <div className="grid grid-cols-[repeat(auto-fit,minmax(10rem,1fr))] gap-3 w-full mb-4 min-w-0">
          <div className="min-w-0">
            <Typography
              level="body-sm"
              className="text-muted-foreground/80 mb-1"
            >
              {INVOICE_LABELS.billedTo}
            </Typography>
            <div className="flex flex-col justify-center min-h-9 w-full min-w-0">
              {/* Billed to an address, it is that address; else its contact,
                  picked here, its chip worded as the invoice prints it. */}
              {document?.payableTo ? (
                invoice.billedTo.map((line, index) => (
                  <Typography
                    key={index}
                    level="body-md"
                    className="text-foreground"
                  >
                    {line}
                  </Typography>
                ))
              ) : (
                <DocumentPreviewField
                  store={store}
                  objectKey="contact"
                  printedAs={invoice.billedTo.join(", ")}
                  cellProps={getCellProps({
                    rowIndex: 1,
                    colIndex: 0,
                    objectKey: "contact",
                  })}
                />
              )}
            </div>
          </div>

          <div className="min-w-0">
            <Typography
              level="body-sm"
              className="text-muted-foreground/80 mb-1"
            >
              {INVOICE_LABELS.issuedBy}
            </Typography>
            <div className="flex flex-col justify-center min-h-9 w-full min-w-0">
              {invoice.issuedBy.map((line, index) => (
                <Typography
                  key={index}
                  level="body-md"
                  className="text-foreground"
                >
                  {line}
                </Typography>
              ))}
            </div>
          </div>
        </div>

        {/* Amount Due Headline */}
        <div className="mb-4">
          <Typography
            level="h2"
            className={cn(
              isCompact ? "text-[1.25rem]" : "text-[1.75rem]",
              "[font-weight:600] [letter-spacing:-0.02em]",
              accentTextClassName,
            )}
          >
            {invoice.headline}
          </Typography>
        </div>

        {/* Notes */}
        <div className="mb-8">
          <DocumentPreviewField
            store={store}
            objectKey="description"
            isMultiline
            style={{ color: "var(--muted-foreground)" }}
            cellProps={getCellProps({
              rowIndex: 2,
              colIndex: 0,
              objectKey: "description",
            })}
          />
        </div>

        {/* Line Items Table */}
        <div className="w-full mb-6">
          <div
            className={cn(
              "grid grid-cols-[1.5fr_0.5fr_0.9fr_1.3fr_1fr_28px]",
              "gap-4 [border-bottom-width:1px] [border-bottom-style:solid]",
              isCompact ? "pt-2 pb-2" : "pt-3 pb-3",
              accent ? "border-(--invoice-accent)" : "border-input",
            )}
          >
            {INVOICE_LABELS.columns.map((label, index) => (
              <Typography
                key={label}
                level="body-sm"
                className={cn("text-foreground", index > 0 && "text-right")}
              >
                {label}
              </Typography>
            ))}
          </div>

          <DocumentPreviewLineItems
            store={store}
            printed={invoice.lines}
            getCellProps={getCellProps}
          />
        </div>

        {/* Totals: the last, what is due, set apart. */}
        <div className="flex justify-end w-full mb-8">
          <div className="w-70">
            {invoice.totals.map(({ label, value }, index) => {
              const isDue = index === invoice.totals.length - 1;
              return (
                <div key={label}>
                  {isDue && <Separator className="mt-2 mb-2" />}
                  <div className="flex justify-between pt-2 pb-2">
                    <Typography
                      level={isDue ? "title-md" : "body-md"}
                      className={
                        isDue
                          ? "text-foreground [font-weight:600]"
                          : "text-muted-foreground"
                      }
                    >
                      {label}
                    </Typography>
                    <Typography
                      level={isDue ? "title-md" : "body-md"}
                      className={
                        isDue
                          ? cn(accentTextClassName, "[font-weight:600]")
                          : "text-foreground"
                      }
                    >
                      {value}
                    </Typography>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Ways to pay: the ones the invoice takes - an e-transfer as its
            template says, a card on the page it is shared at. */}
        {(invoice.eTransfer || invoice.card) && (
          <>
            <Separator className="mb-6" />

            <div className="w-full" data-testid="invoice-ways-to-pay">
              <Typography
                level="title-md"
                className="text-foreground mb-6 [font-weight:600]"
              >
                {INVOICE_LABELS.waysToPay}
              </Typography>

              <div className="flex flex-wrap gap-8">
                {invoice.eTransfer && (
                  <div
                    data-testid="invoice-ways-to-pay-e-transfer"
                    className="flex flex-col gap-1.5 flex-[1_1_14rem] min-w-0"
                  >
                    <Typography
                      level="body-sm"
                      className="text-foreground [font-weight:600]"
                    >
                      {INVOICE_LABELS.eTransfer}
                    </Typography>
                    <Typography
                      level="body-sm"
                      className={cn(
                        invoice.eTransfer.isGiven
                          ? "text-foreground"
                          : "text-muted-foreground/80",
                        "whitespace-pre-wrap",
                      )}
                    >
                      {invoice.eTransfer.instructions}
                    </Typography>
                    {invoice.eTransfer.reference && (
                      <Typography
                        level="body-sm"
                        className="text-muted-foreground"
                      >
                        {invoice.eTransfer.reference}
                      </Typography>
                    )}
                  </div>
                )}

                {/* QR Code for Payment */}
                {invoice.card && (
                  <div className="flex flex-col gap-3">
                    <a
                      href={invoice.card.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={cn(
                        typographyVariants({
                          level: "body-sm",
                        }),
                        "text-primary underline-offset-4 hover:underline",
                        "text-foreground [font-weight:600] [text-decoration:underline]",
                      )}
                    >
                      {INVOICE_LABELS.card}
                    </a>
                    <Typography
                      level="body-sm"
                      className="text-muted-foreground max-w-40"
                    >
                      {INVOICE_LABELS.scan}
                    </Typography>
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.4, ease: "easeOut" }}
                      style={{
                        padding: "12px",
                        backgroundColor: "var(--background)",
                        borderRadius: "8px",
                        border: `1px solid ${"var(--border)"}`,
                        width: "fit-content",
                      }}
                    >
                      <QRCode
                        value={invoice.card.url}
                        size={100}
                        style={{
                          height: "auto",
                          maxWidth: "100%",
                          width: "100px",
                          display: "block",
                        }}
                        viewBox="0 0 256 256"
                        bgColor={"var(--background)"}
                        fgColor={"var(--foreground)"}
                      />
                    </motion.div>
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {/* Its template's line at its foot. */}
        {invoice.footer && (
          <Typography
            level="body-sm"
            data-testid="invoice-footer"
            className={cn(
              "text-muted-foreground whitespace-pre-wrap mt-6 pt-4 [border-top-width:1px] [border-top-style:solid]",
              accent ? "border-(--invoice-accent)" : "border-border",
            )}
          >
            {invoice.footer}
          </Typography>
        )}
      </div>
    </Scope>
  );
}
